import { TrackingCategory } from '@/entities/tracking/const';
import { TrackingType } from '@/entities/tracking/const/type';
import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';
import type { AppOperation } from '@/hooks/tracking/useTrackAppBaseSnapshot';
import { PIPE } from '@/utils/constants/common';

import { appLogger } from '../app-logger/appLogger';
import { toError } from '../errors/toError';

type UpdateOptimizelyExpOptions = {
    markReadyOnSettled?: boolean;
    /**
     * Checked right before the tracking write; return true to abandon it (the
     * variants belong to a page view a navigation has invalidated).
     */
    shouldAbandonWrite?: () => boolean;
    /**
     * Also push `{ siteInfo: { optimizelyExp } }` to `window.adobeDataLayer` so
     * `.getState()` reads return the fresh value for downstream Adobe Launch beacons.
     * DirectAB doesn't need this; the XHR-experiments path in `useTrackAppBaseSnapshot` does.
     */
    pushToAdobeDataLayer?: boolean;
    /**
     * Passed through to `TrackerEngine.snapshot(waitMs)`. Undefined ⇒ helper's 10s default;
     * pass `0` for a synchronous best-effort read — post-bootstrap callers on cached-SSR /
     * pre-consent regions must not stall behind an absent dataHub (PTVWS-6991).
     */
    waitMs?: number;
};

/** Either a variant-token array (`['t1:v1', ...]`) or an `ActiveTests`-shaped map
 *  (`{ t1: 'v1', ... }`). The map form is the shape `ABTestExperimentsProvider` surfaces. */
export type OptimizelyExpVariantsInput = string[] | Record<string, string>;

/**
 * Outcome of a write that ran. `undefined` instead means no write happened — empty input,
 * abandoned, or failed — so callers must not treat the value as emitted. `didChange` is
 * false when the snapshot already held every incoming variant.
 */
export type UpdateOptimizelyExpOutcome = {
    optimizelyExp: string;
    didChange: boolean;
};

const toVariantTokens = (input: OptimizelyExpVariantsInput): string[] =>
    Array.isArray(input) ? input : Object.entries(input).map(([test, key]) => `${test}:${key}`);

export async function updateOptimizelyExp(
    variants: OptimizelyExpVariantsInput,
    {
        markReadyOnSettled = true,
        shouldAbandonWrite,
        pushToAdobeDataLayer = false,
        waitMs,
    }: UpdateOptimizelyExpOptions = {},
): Promise<UpdateOptimizelyExpOutcome | undefined> {
    const variantsForTracking = toVariantTokens(variants);

    if (!variantsForTracking.length) {
        if (!markReadyOnSettled) {
            return;
        }

        BootstrapSignals.mark(BootstrapSignalKeys.OPTIMIZELY_EXP_READY);
        return;
    }

    try {
        const snapshot = await TrackerEngine.snapshot(waitMs);
        if (shouldAbandonWrite?.()) {
            return;
        }
        const appOperation = snapshot.app_operation as AppOperation;
        const optimizelyExp = appOperation?.siteInfo?.optimizelyExp;
        const variantsAlreadyPresent = optimizelyExp ? optimizelyExp.split(PIPE) : [];
        const uniqueVariants = [...new Set([...variantsAlreadyPresent, ...variantsForTracking])];
        const updatedOptimizelyExp = uniqueVariants.join(PIPE);
        // The Set preserves existing order, so an all-present input reproduces the previous
        // string exactly — making string equality a sound "did anything arrive?" test.
        const didChange = updatedOptimizelyExp !== (optimizelyExp ?? '');

        // Pushed even when unchanged: DirectAB writes variants without
        // `pushToAdobeDataLayer`, so the snapshot can hold values this store never received.
        if (pushToAdobeDataLayer && typeof window !== 'undefined') {
            window.adobeDataLayer = window.adobeDataLayer || [];
            window.adobeDataLayer.push({ siteInfo: { optimizelyExp: updatedOptimizelyExp } });
        }

        await TrackerEngine.update({
            type: TrackingType.INFO_UPDATE,
            detail: {
                siteInfo: {
                    optimizelyExp: updatedOptimizelyExp,
                },
            },
            category: TrackingCategory.App.OPERATION,
        });

        return { optimizelyExp: updatedOptimizelyExp, didChange };
    } catch (error) {
        appLogger.error(
            'Error updating optimizelyExp',
            { method: 'updateOptimizelyExp', variantsForTracking },
            toError(error),
        );
        return undefined;
    } finally {
        if (markReadyOnSettled) {
            BootstrapSignals.mark(BootstrapSignalKeys.OPTIMIZELY_EXP_READY);
        }
    }
}
